import React, { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentPreviewModal } from '@/components/AttachmentPreviewModal';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HomeTaskModal } from '@/components/HomeTaskModal';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { CheckCircle } from '@/components/Icons/CheckCircle';
import { Download } from '@/components/Icons/Download';
import { Forward } from '@/components/Icons/Forward';
import { Reply } from '@/components/Icons/Reply';
import { Star } from '@/components/Icons/Star';
import { StarFilled } from '@/components/Icons/StarFilled';
import { Trash } from '@/components/Icons/Trash';
import { Snackbar } from '@/components/Snackbar';
import { PreschoolEvaluationModal } from '@/components/PreschoolEvaluationModal';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { MailAttachmentCard } from '@/pages/Mail/components/MailAttachmentCard';
import { MailDetailRecipients } from '@/pages/Mail/components/MailDetailRecipients';
import { MailPdfDocument } from '@/pages/Mail/components/MailPdfDocument';
import { useEvaluationReportPreview } from '@/pages/Mail/hooks/useEvaluationReportPreview';
import { useIndividualSupportPlan } from '@/pages/Mail/hooks/useIndividualSupportPlan';
import { SYSTEM_AUTHOR_USER_ID } from '@/pages/Mail/mailMessages';
import {
  ButtonVariant,
  FollowUpStatus,
  SnackbarVariant,
} from '@/types/components';
import type { MailAttachment, MailMessage } from '@/types/mail';
import { openAttachment } from '@/utils/attachments';
import { formatDayShortMonthYearTime } from '@/utils/date';
import { triggerDownload } from '@/utils/file';
import { sanitizeRichTextHtml } from '@/utils/html';
import {
  getAttachmentLinkId,
  getFinalEvaluationReportParameters,
  getHomeTaskId,
  hasEvaluationReportPath,
  hasIndividualSupportPlanPath,
  hasPreschoolEvaluationPath,
} from '@/utils/mail';
import { exportElementToPdf, getMailPdfFileName } from '@/utils/pdf';

const followUpIcon: Record<FollowUpStatus, ReactNode> = {
  [FollowUpStatus.None]: <Star className="size-5 text-muted-foreground" />,
  [FollowUpStatus.Marked]: <StarFilled className="size-5 text-accent" />,
  [FollowUpStatus.Completed]: (
    <CheckCircle className="size-5 text-successful" />
  ),
};

interface SnackbarState {
  message: string;
  variant: SnackbarVariant;
}

interface Props {
  mail: MailMessage;
  isMobile: boolean;
  isLoading: boolean;
  showFollowUp: boolean;
  showReplyForward: boolean;
  canDelete: boolean;
  onBack: () => void;
  onCycleFollowUp: (id: string) => void;
  onDelete: (id: string) => void;
  onReply: (mail: MailMessage) => void;
  onForward: (mail: MailMessage) => void;
  onOpenAttachment: (attachment: MailAttachment) => void;
}

export const MailDetail: React.FC<Props> = ({
  mail,
  isMobile,
  isLoading,
  showFollowUp,
  showReplyForward,
  canDelete,
  onBack,
  onCycleFollowUp,
  onDelete,
  onReply,
  onForward,
  onOpenAttachment,
}) => {
  const { t } = useTranslation();
  const [isRecipientsExpanded, setIsRecipientsExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>();
  const [isEvaluationOpen, setIsEvaluationOpen] = useState(false);
  const [openHomeTaskId, setOpenHomeTaskId] = useState<number | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  const showSuccess = (message: string) =>
    setSnackbar({ message, variant: SnackbarVariant.Success });

  const showError = (message: string) =>
    setSnackbar({ message, variant: SnackbarVariant.Error });

  const {
    isReportOpen,
    reportUrl,
    reportFileName,
    openStudyYearReport,
    openFinalReport,
    closeReport,
  } = useEvaluationReportPreview(() => showError(t('global.errorOccurred')));
  const { isConfirmOpen, openConfirm, closeConfirm, confirmPlan } =
    useIndividualSupportPlan({
      onSuccess: () =>
        showSuccess(t('mail.detail.individualSupportPlan.success')),
      onFailure: () => showError(t('mail.detail.individualSupportPlan.error')),
    });

  const hasRecipients = mail.recipients.length > 0;
  const hasMultipleRecipients = mail.recipients.length > 1;
  const hasAttachments = mail.attachments.length > 0;
  const canReply =
    showReplyForward && mail.authorUserId !== SYSTEM_AUTHOR_USER_ID;

  const handleDownload = async () => {
    if (!pdfRef.current || isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      await exportElementToPdf(
        pdfRef.current,
        getMailPdfFileName(mail.subject, mail.timeCreated),
      );
      showSuccess(t('mail.detail.documentSaved'));
    } catch {
      showError(t('global.errorOccurred'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleBodyClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const anchor = event.target.closest('a');

    if (!anchor) {
      return;
    }

    const homeTaskId = getHomeTaskId(anchor);

    if (homeTaskId) {
      event.preventDefault();
      setOpenHomeTaskId(homeTaskId);
      return;
    }

    const href = anchor.getAttribute('href');

    if (!href) {
      return;
    }

    if (hasEvaluationReportPath(href)) {
      event.preventDefault();
      openStudyYearReport();
      return;
    }

    const finalReportParameters = getFinalEvaluationReportParameters(href);

    if (finalReportParameters) {
      event.preventDefault();
      openFinalReport(finalReportParameters);
      return;
    }

    if (hasIndividualSupportPlanPath(href)) {
      event.preventDefault();
      openConfirm();
      return;
    }

    if (hasPreschoolEvaluationPath(href)) {
      event.preventDefault();
      setIsEvaluationOpen(true);
      return;
    }

    const attachmentId = getAttachmentLinkId(href);

    if (attachmentId) {
      event.preventDefault();
      await openAttachment({
        attachment: {
          id: attachmentId,
          name: anchor.text.trim(),
          sizeInBytes: 0,
        },
        onPreview: onOpenAttachment,
      });
    }
  };

  const handleReportDownload = () => {
    if (!reportUrl) {
      return;
    }

    triggerDownload(reportUrl, reportFileName);
    showSuccess(t('mail.detail.documentSaved'));
    closeReport();
  };

  const detailsHeader = (
    <>
      <div className="flex items-start justify-between gap-4">
        <p className="min-w-0 flex-1 text-subhead-14 text-foreground">
          {mail.senderName}
        </p>
        <p className="shrink-0 text-body-14 text-subtle-foreground">
          {formatDayShortMonthYearTime(mail.timeCreated)}
        </p>
      </div>
      {hasRecipients && (
        <MailDetailRecipients
          recipients={mail.recipients}
          isExpanded={isRecipientsExpanded}
        />
      )}
    </>
  );

  const replyForward = (
    <>
      {canReply && (
        <Button variant={ButtonVariant.Secondary} onClick={() => onReply(mail)}>
          <Reply className="size-6" />
          {t('mail.detail.reply')}
        </Button>
      )}
      {showReplyForward && (
        <Button
          variant={ButtonVariant.Secondary}
          onClick={() => onForward(mail)}
        >
          <Forward className="size-6" />
          {t('mail.detail.forward')}
        </Button>
      )}
    </>
  );

  const body = (
    <div className="flex flex-col gap-14 pb-8">
      <div className="flex flex-col gap-6">
        {hasMultipleRecipients && (
          <button
            type="button"
            onClick={() => setIsRecipientsExpanded((value) => !value)}
            className="flex cursor-pointer flex-col gap-0.5 border-b border-divider px-5 pt-6 pb-6 text-left"
          >
            {detailsHeader}
          </button>
        )}
        {!hasMultipleRecipients && (
          <div className="flex flex-col gap-0.5 border-b border-divider px-5 pt-6 pb-6">
            {detailsHeader}
          </div>
        )}

        <div className="flex flex-col gap-6 px-5">
          <h1 className="wrap-anywhere text-headline-20 text-foreground">
            {mail.subject}
          </h1>
          <div
            className="rich-text text-body-16 text-foreground"
            dangerouslySetInnerHTML={{
              __html: sanitizeRichTextHtml(mail.body),
            }}
            onClick={handleBodyClick}
          />
        </div>
      </div>

      {hasAttachments && (
        <div className="flex flex-wrap gap-5 px-5">
          {mail.attachments.map((attachment) => (
            <MailAttachmentCard
              key={attachment.id}
              attachment={attachment}
              onPreview={onOpenAttachment}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-divider px-5 py-2">
        <button
          type="button"
          onClick={onBack}
          className="flex cursor-pointer items-center justify-center"
        >
          <ArrowLeft className="size-6 text-foreground" />
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleDownload}
            className="flex cursor-pointer items-center justify-center"
          >
            <Download className="size-5 text-subtle-foreground" />
          </button>
          {showFollowUp && (
            <button
              type="button"
              onClick={() => onCycleFollowUp(mail.id)}
              className="flex cursor-pointer items-center justify-center"
            >
              {followUpIcon[mail.followUpStatus]}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => onDelete(mail.id)}
              className="flex cursor-pointer items-center justify-center"
            >
              <Trash className="size-5 text-subtle-foreground" />
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex flex-1 items-center justify-center py-20">
          <PageSpinner className="size-[22px] text-subtle-foreground" />
        </div>
      )}

      {!isLoading && (
        <AutoHideScrollArea
          hasTopShadow
          hasBottomShadow
          className="flex min-h-0 flex-1 flex-col"
        >
          {body}
        </AutoHideScrollArea>
      )}

      {!isLoading && isMobile && showReplyForward && (
        <div className="flex shrink-0 gap-3 border-t border-divider bg-card px-5 pt-4 pb-[calc(16px+env(safe-area-inset-bottom))] [&>button]:flex-1">
          {replyForward}
        </div>
      )}

      {!isLoading && !isMobile && showReplyForward && (
        <div className="flex shrink-0 items-center gap-3 bg-card px-5 py-4">
          {replyForward}
        </div>
      )}

      <div
        ref={pdfRef}
        aria-hidden
        className="pointer-events-none fixed -left-[9999px] top-0"
      >
        <MailPdfDocument mail={mail} />
      </div>

      {isReportOpen && (
        <AttachmentPreviewModal
          name={reportFileName}
          url={reportUrl}
          onClose={closeReport}
          onDownload={handleReportDownload}
        />
      )}

      {openHomeTaskId !== null && (
        <HomeTaskModal
          homeTaskId={openHomeTaskId}
          onClose={() => setOpenHomeTaskId(null)}
        />
      )}

      {isEvaluationOpen && (
        <PreschoolEvaluationModal
          isOpen
          onClose={() => setIsEvaluationOpen(false)}
          onApproveFailure={() => showError(t('global.errorOccurred'))}
        />
      )}

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title={t('mail.detail.individualSupportPlan.title')}
        message={t('mail.detail.individualSupportPlan.message')}
        confirmLabel={t('preschoolEvaluation.approve')}
        onClose={closeConfirm}
        onConfirm={confirmPlan}
      />

      <Snackbar
        isOpen={!!snackbar}
        message={snackbar?.message ?? ''}
        variant={snackbar?.variant}
        onDismiss={() => setSnackbar(undefined)}
      />
    </div>
  );
};
